import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Loader2, LogOut, Lock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const http = axios.create({ baseURL: API, withCredentials: true });
const PERIODS = [["24h", "Last 24 hours"], ["7d", "Last 7 days"], ["30d", "Last 30 days"]];
const CLICK_LABEL = { love: "Love University", bootcamp: "Bootcamp", dasha: "8-week" };

const errText = (e) => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg).filter(Boolean).join(" ");
  return "Something went wrong. Please try again.";
};
const pct = (a, b) => (b ? `${Math.round((a / b) * 100)}%` : "—");

const Login = ({ onIn }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      const { data } = await http.post("/auth/login", { email, password });
      onIn(data);
    } catch (x) {
      setErr(errText(x));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form className="st-login" onSubmit={submit} data-testid="stats-login-form">
      <p className="st-kicker"><Lock size={14} /> Private</p>
      <h1 className="st-h1">Page stats</h1>
      <input className="st-input" type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="username" data-testid="stats-login-email" />
      <input className="st-input" type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" data-testid="stats-login-password" />
      <button className="btn-gold w-full" disabled={busy} data-testid="stats-login-submit">{busy ? <Loader2 size={18} className="spin" /> : "Sign in"}</button>
      {err && <p className="server-error" role="alert" data-testid="stats-login-error">{err}</p>}
    </form>
  );
};

const Step = ({ label, value, prev, prevLabel, id }) => (
  <div className="st-step" data-testid={`funnel-${id}`}>
    {prev !== undefined && <p className="st-rate">{pct(value, prev)} of {prevLabel}</p>}
    <div className="st-box"><span className="st-label">{label}</span><span className="st-num" data-testid={`funnel-${id}-count`}>{value}</span></div>
  </div>
);

const Funnel = ({ c, clicked }) => (
  <div className="st-funnel" data-testid="stats-funnel">
    <Step id="landed" label="Landed" value={c.landed} />
    <Step id="teaser" label="Played teaser" value={c.teaser_play} prev={c.landed} prevLabel="visitors" />
    <Step id="signup" label="Signed up" value={c.signup} prev={c.teaser_play} prevLabel="teaser viewers" />
    <Step id="class" label="Played 60-min class" value={c.class_play} prev={c.signup} prevLabel="sign-ups" />
    <Step id="clicked" label="Clicked an offer" value={clicked} prev={c.class_play} prevLabel="class viewers" />
    <div className="st-split" data-testid="funnel-offer-split">
      <span>Love University <b>{c.click_love}</b></span><span>Bootcamp <b>{c.click_bootcamp}</b></span><span>8-week <b>{c.click_dasha}</b></span>
    </div>
  </div>
);

const Signups = ({ rows }) => (
  <div className="st-table" data-testid="stats-signups">
    {rows.length === 0 && <p className="st-empty">No sign-ups in this period yet.</p>}
    {rows.map((r) => (
      <div className="st-row" key={r.email} data-testid="stats-signup-row">
        <div><b>{r.first_name}</b><span>{r.email}</span><span>{r.phone}</span></div>
        <div className="st-meta">
          <span>{new Date(r.updated_at).toLocaleString()}</span>
          <span className={r.played_class ? "st-yes" : "st-no"}>{r.played_class ? "Played class" : "Not played"}</span>
          <span>{r.clicked.length ? r.clicked.map((k) => CLICK_LABEL[k] || k).join(", ") : "No offer clicks"}</span>
        </div>
      </div>
    ))}
  </div>
);

const Dashboard = ({ onOut }) => {
  const [period, setPeriod] = useState("24h");
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const load = useCallback(async (p) => {
    setErr("");
    try {
      const { data: d } = await http.get("/admin/stats", { params: { period: p } });
      setData(d);
    } catch (x) {
      if (x?.response?.status === 401) onOut();
      else setErr(errText(x));
    }
  }, [onOut]);
  useEffect(() => { load(period); }, [period, load]);
  const logout = async () => { await http.post("/auth/logout").catch(() => {}); onOut(); };
  const c = data?.counts || {};
  return (
    <div className="st-dash" data-testid="stats-dashboard">
      <div className="st-top">
        <h1 className="st-h1">Page stats</h1>
        <button className="st-out" onClick={logout} data-testid="stats-logout"><LogOut size={15} /> Sign out</button>
      </div>
      <div className="st-tabs" role="tablist">
        {PERIODS.map(([k, l]) => <button key={k} role="tab" aria-selected={period === k} className={period === k ? "on" : ""} onClick={() => setPeriod(k)} data-testid={`stats-period-${k}`}>{l}</button>)}
      </div>
      {err && <p className="server-error">{err}</p>}
      {!data ? <Loader2 className="spin st-spin" /> : (
        <>
          <Funnel c={c} clicked={data.clicked_any ?? 0} />
          <h2 className="st-h2">Recent sign-ups</h2>
          <Signups rows={Array.isArray(data.signups) ? data.signups : []} />
        </>
      )}
      <p className="st-note">Counting started when this stats page went live, so there’s no history from before that. Each action counts once per visitor per day.</p>
    </div>
  );
};

export default function StatsPage() {
  const [user, setUser] = useState(null);
  useEffect(() => {
    http.get("/auth/me").then((r) => setUser(r.data)).catch(() => setUser(false));
  }, []);
  return (
    <main className="st-page">
      {user === null ? <Loader2 className="spin st-spin" /> : user ? <Dashboard onOut={() => setUser(false)} /> : <Login onIn={setUser} />}
    </main>
  );
}
